import { SelectablePanel } from '@app/components/base/selectable-panel';
import { navigateTo, pushTo } from '@app/core/routing';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { getOauthConnectUrl } from '../get-oauth-connect-url';
import { ConnectAlternative } from './connect-alternative';
import { connectAlternativeIcons } from './connect-alternative-icons';
import { connectAlternativesMessages as messages } from './connect-alternatives-messages';

const REDIRECT_FEEDBACK_DELAY_MS = 300;

type Props = {
  platform: SocialPlatform;
  alternative: ConnectAlternative;
  /** Disables interaction while a redirect is in progress (any option was clicked). */
  disabled?: boolean;
  /** Whether this option is the one currently redirecting to the platform. */
  redirecting?: boolean;
  onSelect?: () => void;
};

/**
 * Runs the connect alternative action: full redirect for OAuth, in-app history push for routes.
 */
function runAlternativeAction(platform: SocialPlatform, alternative: ConnectAlternative) {
  if (alternative.action.type === 'oauth') {
    navigateTo(getOauthConnectUrl(platform, alternative.action.loginSuffix));
    return;
  }
  pushTo(alternative.action.to);
}

/**
 * Clickable connect alternative: icon, optional recommended badge, description, chevron.
 */
export function ConnectAlternativeOption({
  platform,
  alternative,
  disabled,
  redirecting,
  onSelect,
}: Props) {
  const { $t } = useIntl();
  const isRecommended = Boolean(alternative.recommended);
  const Icon = connectAlternativeIcons[alternative.icon];

  return (
    <SelectablePanel
      disabled={disabled}
      onClick={() => {
        onSelect?.();
        // Let the locked/loading state render before leaving the current page.
        window.setTimeout(
          () => runAlternativeAction(platform, alternative),
          REDIRECT_FEEDBACK_DELAY_MS,
        );
      }}
      sx={{
        alignItems: 'flex-start',
        p: 2,
      }}
    >
      {/* Optical align with subtitle1 line box: (28px line − 20px icon) / 2 ≈ 4px */}
      <Icon color="action" fontSize="small" sx={{ mt: 0.5, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1">{$t(alternative.name)}</Typography>
          {isRecommended && (
            <Chip
              size="small"
              color="primary"
              label={$t(messages.recommendedBadge)}
              sx={{ height: 22 }}
              variant="outlined"
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {$t(alternative.description)}
        </Typography>
        {alternative.permissions && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            <FormattedMessage {...messages.permissionsLabel} />
            {': '}
            {$t(alternative.permissions)}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: 24,
          height: 24,
          alignSelf: 'center',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {redirecting ? (
          <CircularProgress size={20} />
        ) : (
          <ChevronRightIcon
            color="action"
            // Keep layout space when other options lock, so the row does not jump.
            sx={{ visibility: disabled ? 'hidden' : 'visible' }}
          />
        )}
      </Box>
    </SelectablePanel>
  );
}
